/**
 * Account page for user profile settings
 * Displays and allows editing of email, password, and account preferences
 */
/* eslint-disable max-lines-per-function */

import { Mail, Shield, User } from "lucide-react";
import { useState, type ReactElement } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { apiCall } from "../../config/api";
import { Button } from "../../shared/components/ui/button";
import { Card } from "../../shared/components/ui/card";
import { Input } from "../../shared/components/ui/input";
import { Label } from "../../shared/components/ui/label";
import { useToast } from "../../shared/hooks/use-toast";

export function Account(): ReactElement {
  const { user, updateUser } = useAuth();
  const { toast } = useToast();
  const [displayName, setDisplayName] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const handleSaveProfile = async () => {
    setIsSaving(true);

    const newDisplayName = displayName.trim() || null;

    // Optimistic update
    if (newDisplayName !== null) {
      updateUser({ displayName: newDisplayName });
    }

    try {
      const response = await apiCall("/v1/user/onboarding", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          displayName: newDisplayName,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update profile");
      }

      toast({
        description: "Profile updated successfully",
        variant: "default",
      });
    } catch (error) {
      console.error("Failed to update profile:", error);
      // Revert optimistic update on error
      updateUser({ displayName: user?.displayName });
      toast({
        description: "Failed to update profile",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="space-y-2">
        <h1 className="text-2xl font-bold text-white mb-2">Account Settings</h1>
        <p className="text-xs text-neutral-400 pb-6">Manage your account information and preferences</p>
      </div>

      {/* Profile Information Card */}
      <Card className="bg-transparent border-neutral-800 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-lumi-accent/10 rounded-lg">
            <User className="h-5 w-5 text-lumi-accent" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">Profile Information</h2>
            <p className="text-sm text-neutral-400">Update your display name and personal details</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <Label htmlFor="displayName">Display Name</Label>
            <Input
              id="displayName"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Enter your name"
              className="mt-2"
            />
          </div>

          <Button onClick={() => void handleSaveProfile()} disabled={isSaving} className="mt-4" variant="outline">
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </Card>

      {/* Email & Authentication Card */}
      <Card className="bg-transparent border-neutral-800 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-lumi-accent/10 rounded-lg">
            <Mail className="h-5 w-5 text-lumi-accent" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">Email & Authentication</h2>
            <p className="text-sm text-neutral-400">Manage your login credentials</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              value={user?.email || ""}
              disabled
              className="mt-2 bg-neutral-800/50 cursor-not-allowed"
            />
            <p className="text-xs text-neutral-500 mt-2">Email address cannot be changed at this time</p>
          </div>

          <div className="pt-4 border-t border-neutral-800">
            <Link to="/forgot-password">
              <Button variant="outline" className="w-full sm:w-auto">
                Change Password
              </Button>
            </Link>
          </div>
        </div>
      </Card>

      {/* Account Security Card */}
      <Card className="bg-transparent border-neutral-800 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-lumi-accent/10 rounded-lg">
            <Shield className="h-5 w-5 text-lumi-accent" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">Account Security</h2>
            <p className="text-sm text-neutral-400">Account status and role information</p>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex justify-between items-center py-2">
            <span className="text-neutral-300">Account Status</span>
            <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs font-medium rounded-full">Active</span>
          </div>
          <div className="flex justify-between items-center py-2 border-t border-neutral-800">
            <span className="text-neutral-300">Role</span>
            <span className="px-3 py-1 bg-neutral-800 text-neutral-300 text-xs font-medium rounded-full">
              {user?.role || "VIEWER"}
            </span>
          </div>
        </div>
      </Card>
    </div>
  );
}
